import { useRef, useState, type FormEvent } from "react";
import { NumberSearch } from "./CarriersPanel";
import { PlanChoice, type SelectedPlan } from "./AccountPlanPanel";
import { ExistingNumberPicker } from "./ServiceTools";
type Api = <T>(
  path: string,
  options?: {
    method?: string;
    body?: unknown;
    csrf?: string | undefined;
    signal?: AbortSignal;
  },
) => Promise<T>;
export function NewCustomerForm({
  api,
  csrf,
  onCreated,
}: {
  api: Api;
  csrf: string | undefined;
  onCreated: (account: { id: string }) => Promise<void>;
}) {
  const [details, setDetails] = useState<Record<string, string> | null>(null);
  const [stage, setStage] = useState("details");
  const [choice, setChoice] = useState("owned");
  const [selected, setSelected] = useState("");
  const [existing, setExisting] = useState("");
  const [carrier, setCarrier] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [created, setCreated] = useState<{ id: string } | null>(null);
  const [ownedLinked, setOwnedLinked] = useState(false);
  const [plan, setPlan] = useState<SelectedPlan | null>(null);
  const receipt = useRef({ hash: "", key: crypto.randomUUID() });
  async function save(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    if (!details) return;
    const numberRequest =
      choice === "new"
        ? { mode: "new", number: selected, source: "twilio" }
        : choice === "existing"
          ? { mode: "existing", number: existing, carrier }
          : { mode: "later" };
    const value = {
      name: details.name,
      contactName: details.contactName,
      billingEmail: details.billingEmail,
      kind: details.kind,
      address: {
        line1: details.line1,
        line2: details.line2 ?? "",
        city: details.city,
        region: details.region,
        postalCode: details.postalCode,
      },
      numberRequest,
      ...(plan ? { planSelection: plan } : {}),
    };
    const hash = JSON.stringify(value);
    if (receipt.current.hash !== hash)
      receipt.current = { hash, key: crypto.randomUUID() };
    setBusy(true);
    setError("");
    try {
      const result = await api<{ id: string }>("/admin/accounts", {
        method: "POST",
        csrf,
        body: { ...value, requestKey: receipt.current.key },
      });
      setCreated(result);
      if (choice === "owned") return;
      try {
        await onCreated(result);
      } catch {
        setError(
          "Customer saved, but the account could not be opened. Use Open customer below.",
        );
      }
    } catch (cause) {
      setError((cause as Error).message);
    } finally {
      setBusy(false);
    }
  }
  return (
    <section className="cr-panel">
      <h2>Add a customer</h2>
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
      {created ? (
        <>
          <p role="status">
            {choice === "owned"
              ? ownedLinked
                ? "Owned number linked. Continue to the customer account to add equipment and finish service setup."
                : "Customer saved. Choose an owned number below to link it to this account."
              : "Customer and number preference saved. No carrier service was changed."}
          </p>
          {choice === "owned" && !ownedLinked && (
            <ExistingNumberPicker
              api={api}
              csrf={csrf}
              accountId={created.id}
              customer={false}
              initiallyOpen
              onSaved={async () => setOwnedLinked(true)}
            />
          )}
          <button
            className="cr-button"
            onClick={() => {
              void onCreated(created).catch(() =>
                setError(
                  "The account could not be opened. Refresh Customers to retry.",
                ),
              );
            }}
          >
            {choice === "owned"
              ? "Continue to customer account"
              : "Open customer"}
          </button>
        </>
      ) : stage === "details" ? (
        <form
          className="cr-form"
          onSubmit={(event) => {
            event.preventDefault();
            const value = Object.fromEntries(
              new FormData(event.currentTarget),
            ) as Record<string, string>;
            if (
              details &&
              (details.city !== value.city || details.region !== value.region)
            )
              setSelected("");
            if (details?.kind !== value.kind) setPlan(null);
            setDetails(value);
            setStage("number");
            setError("");
          }}
        >
          <div className="cr-form-grid">
            <label className="cr-field">
              <span>Customer / company name</span>
              <input
                name="name"
                required
                maxLength={200}
                defaultValue={details?.name}
              />
            </label>
            <label className="cr-field">
              <span>Contact name</span>
              <input
                name="contactName"
                required
                maxLength={120}
                defaultValue={details?.contactName}
                autoComplete="name"
              />
            </label>
            <label className="cr-field">
              <span>Billing email</span>
              <input
                name="billingEmail"
                type="email"
                required
                maxLength={254}
                defaultValue={details?.billingEmail}
                autoComplete="email"
              />
            </label>
            <label className="cr-field">
              <span>Customer type</span>
              <select name="kind" defaultValue={details?.kind ?? "residential"}>
                <option value="residential">Residential</option>
                <option value="commercial">Commercial</option>
                <option value="enterprise">Enterprise · MozartCX</option>
              </select>
            </label>
          </div>
          <h3>Service address</h3>
          <label className="cr-field">
            <span>Street address</span>
            <input
              name="line1"
              required
              maxLength={200}
              defaultValue={details?.line1}
              autoComplete="address-line1"
            />
          </label>
          <div className="cr-form-grid">
            <label className="cr-field">
              <span>Apartment, suite or unit (optional)</span>
              <input
                name="line2"
                maxLength={120}
                defaultValue={details?.line2}
                autoComplete="address-line2"
              />
            </label>
            <label className="cr-field">
              <span>City</span>
              <input
                name="city"
                required
                maxLength={100}
                defaultValue={details?.city}
                autoComplete="address-level2"
              />
            </label>
            <label className="cr-field">
              <span>State</span>
              <input
                name="region"
                required
                minLength={2}
                maxLength={2}
                pattern="[A-Za-z]{2}"
                defaultValue={details?.region}
                placeholder="Two-letter abbreviation"
                autoComplete="address-level1"
              />
            </label>
            <label className="cr-field">
              <span>ZIP code</span>
              <input
                name="postalCode"
                required
                maxLength={10}
                pattern="[0-9]{5}(-[0-9]{4})?"
                defaultValue={details?.postalCode}
                autoComplete="postal-code"
              />
            </label>
          </div>
          <button className="cr-button">Continue to telephone number</button>
        </form>
      ) : (
        <>
          <p>
            <strong>{details?.name}</strong>
            <br />
            {[
              details?.line1,
              details?.line2,
              details?.city,
              details?.region,
              details?.postalCode,
            ].join(", ")}
          </p>
          <button
            className="cr-text-button"
            disabled={busy}
            onClick={() => setStage("details")}
          >
            Edit customer details
          </button>
          <label className="cr-field">
            <span>Telephone number</span>
            <select
              value={choice}
              disabled={busy}
              onChange={(event) => setChoice(event.target.value)}
            >
              <option value="owned">Choose from our owned numbers</option>
              <option value="new">Find a new number from Twilio</option>
              <option value="existing">
                Keep the customer's current number
              </option>
              <option value="later">Choose later</option>
            </select>
          </label>
          {choice === "owned" && (
            <p>
              Save the customer first, then choose an available number from our
              verified Twilio inventory. No port or number purchase is needed.
            </p>
          )}
          {choice === "new" && !selected && (
            <NumberSearch
              api={api}
              address={{ city: details!.city!, state: details!.region! }}
              onSelect={setSelected}
            />
          )}
          <form className="cr-form" onSubmit={save}>
            <fieldset className="cr-form-fields" disabled={busy}>
              <PlanChoice
                api={api}
                kind={details!.kind!}
                value={plan}
                onChange={setPlan}
                disabled={busy}
              />
              {choice === "new" && selected && (
                <>
                  <p role="status">
                    Preferred number: <strong>{selected}</strong>. Not reserved.
                  </p>
                  <button
                    type="button"
                    className="cr-text-button"
                    onClick={() => setSelected("")}
                  >
                    Choose a different number
                  </button>
                </>
              )}
              {choice === "existing" && (
                <>
                  <label className="cr-field">
                    <span>Number to keep</span>
                    <input
                      type="tel"
                      required
                      maxLength={30}
                      value={existing}
                      onChange={(event) => setExisting(event.target.value)}
                    />
                  </label>
                  <label className="cr-field">
                    <span>Current carrier</span>
                    <input
                      required
                      maxLength={120}
                      value={carrier}
                      onChange={(event) => setCarrier(event.target.value)}
                    />
                  </label>
                  <p>
                    If the number stays with its carrier, staff will arrange the
                    phone-system change. This does not submit a carrier port.
                  </p>
                </>
              )}
              <p className="cr-form-note">
                Saves the customer, service address and number preference.
                Address validation, number purchase or transfer, and phone
                activation are separate. No carrier charges are incurred here.
              </p>
              <button
                className="cr-button"
                disabled={choice === "new" && !selected}
              >
                {busy
                  ? "Saving customer…"
                  : choice === "owned"
                    ? "Save customer and choose an owned number"
                    : "Save customer"}
              </button>
            </fieldset>
          </form>
        </>
      )}
    </section>
  );
}
